import React, { useState } from "react";
import { useMoney } from "../../hooks/useMoney";
import { Loader2, Calendar, DollarSign, Wallet, Plus, CheckCircle, Download } from "lucide-react";
import * as XLSX from "xlsx";
import CustomModal from "../common/CustomModal";
import type { WalletFySummaryItem, DistributorTransaction } from "../../features/distributor/DistributorSlice";
import { fetchWalletFyTransactions } from "../../features/distributor/DistributorSlice";
import { useAppDispatch } from "../../store/hook";

interface PreviousFySummaryModalProps {
  show: boolean;
  onHide: () => void;
  summaries: WalletFySummaryItem[] | null;
  loading: boolean;
  error: string | null;
  programName: string;
  distributorId: number;
  programId: string;
}

const formatDate = (dateString: string): string => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const PreviousFySummaryModal: React.FC<PreviousFySummaryModalProps> = ({
  show,
  onHide,
  summaries,
  loading,
  error,
  programName,
  distributorId,
  programId,
}) => {
  const dispatch = useAppDispatch();
  const { symbol, format: formatAmount } = useMoney();
  const [downloadingFyKey, setDownloadingFyKey] = useState<string | null>(null);

  const getFyKey = (item: WalletFySummaryItem) => String(item.summaryId);

  const handleDownloadTransactions = async (item: WalletFySummaryItem) => {
    const key = getFyKey(item);
    setDownloadingFyKey(key);
    try {
      const response = await dispatch(
        fetchWalletFyTransactions({
          distributorId,
          walletId: item.walletId,
          fySummaryId: item.summaryId,
          page: 1,
          limit: 10000,
          sortBy: "createdAt",
          sortOrder: "DESC",
        })
      );
      if (!fetchWalletFyTransactions.fulfilled.match(response)) {
        alert("Failed to fetch transactions");
        return;
      }
      const data = response.payload;
      const transactions = data?.transactions || [];
      if (transactions.length === 0) {
        alert("No transactions found for this FY to download");
        return;
      }
      const exportData = transactions.map((tx: DistributorTransaction) => {
        const date =
          String(tx.transactionTypeDisplay || "").toUpperCase() === "DEBIT"
            ? tx.debitDate
            : String(tx.transactionTypeDisplay || "").toUpperCase() === "SETTLEMENT"
              ? tx.settlementDate
              : tx.createdAt;
        let formattedDate = "";
        if (date) {
          const dateObj = new Date(date);
          formattedDate = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, "0")}-${String(dateObj.getDate()).padStart(2, "0")}`;
        }
        return {
          "Date Created": formattedDate,
          "Transaction Type": tx.transactionTypeDisplay || "",
          "Top Up": tx.topUpDelta ?? "",
          Balance: tx.balanceDelta ?? "",
          Program: tx["program.programName"] ?? "",
          Bank: tx["banks.displayName"] ?? "",
          "Bank Reference": tx.transactionNumber ?? "",
          "Transaction Remark": tx.remark ?? "",
        };
      });
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Transactions");
      const fyLabel = `${item.financialYearStart.slice(0, 4)}-${item.financialYearEnd.slice(0, 4)}`;
      XLSX.writeFile(wb, `${programName}_FY_${fyLabel}_transactions.xlsx`);
    } catch (e) {
      console.error(e);
      alert("Failed to download transactions");
    } finally {
      setDownloadingFyKey(null);
    }
  };

  return (
    <CustomModal
      show={show}
      onHide={onHide}
      title={`Previous FY Summary - ${programName}`}
      width="max-w-4xl w-[88vw]"
    >
      <div className="w-full flex flex-col min-h-0 flex-1 overflow-hidden px-2 py-1">
        {loading && (
          <div className="flex justify-center items-center py-10 border border-gray-100 rounded-lg">
            <Loader2 className="w-6 h-6 animate-spin text-brand/80" />
            <span className="ml-2 text-xs text-gray-500">Loading FY summary...</span>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-xs">
            {error}
          </div>
        )}

        {!loading && !error && summaries && summaries.length === 0 && (
          <div className="text-center py-10 text-gray-500 text-sm border border-gray-100 rounded-lg">
            No previous FY summary found for this wallet.
          </div>
        )}

        {!loading && !error && summaries && summaries.length > 0 && (
          <div className="space-y-5 overflow-y-auto pr-2 pb-4 pt-2" style={{ maxHeight: "75vh" }}>
            {[...summaries]
              .sort(
                (a, b) =>
                  new Date(b.financialYearEnd).getTime() - new Date(a.financialYearEnd).getTime()
              )
              .map((item) => (
              <div
                key={item.summaryId}
                className="relative border border-cardborder rounded-lg bg-white flex flex-col overflow-hidden"
              >
                {/* Wallet FY card header - light background */}
                <div className="bg-white border-b border-gray-100 px-5 py-3.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center justify-center w-7 h-7 rounded-md bg-gray-50 border border-gray-100">
                      <Calendar className="w-3.5 h-3.5 text-gray-500" />
                    </div>
                    <div>
                      <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">
                        Financial Year
                      </p>
                      <p className="text-sm font-semibold text-gray-900">
                        {formatDate(item.financialYearStart)} – {formatDate(item.financialYearEnd)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      type="button"
                      onClick={() => handleDownloadTransactions(item)}
                      disabled={downloadingFyKey === getFyKey(item)}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-brand border border-brand rounded-md hover:bg-brand hover:text-white focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {downloadingFyKey === getFyKey(item) ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Download className="w-3.5 h-3.5" />
                      )}
                      Download transactions
                    </button>
                  </div>
                </div>
                {/* Two-column layout matching AvailableWalletCard */}
                <div className="flex flex-row gap-5 p-5">
                  <div className="flex-1">
                    <h4 className="text-xs font-medium text-gray-700 mb-2">Wallet Details</h4>
                    <div className="flex flex-col gap-1.5">
                      <div className="flex items-center p-2 bg-gray-50 rounded-md">
                        <div className="p-1.5 mr-3 rounded-full bg-white">
                          <DollarSign className="w-3.5 h-3.5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-[11px] text-gray-600 font-medium">Closing Balance</p>
                          <p className="text-sm mt-0.5 font-semibold text-gray-900">
                            {formatAmount(item.closingBalance)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center p-2 bg-gray-50 rounded-md w-full">
                        <div className="p-1.5 mr-3 rounded-full bg-white">
                          <Wallet className="w-3.5 h-3.5 text-green-600" />
                        </div>
                        <div>
                          <p className="text-[11px] text-gray-600 font-medium">Face Value</p>
                          <p className="text-sm mt-0.5 font-semibold text-gray-900">
                            {formatAmount(item.totalFaceValue)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center p-2 bg-gray-50 rounded-md">
                        <div className="p-1.5 mr-3 rounded-full bg-white">
                          <Plus className="w-3.5 h-3.5 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-[11px] text-gray-600 font-medium">Total Top Up</p>
                          <p className="text-sm mt-0.5 font-semibold text-gray-900">
                            {formatAmount(item.totalTopUp)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-xs font-medium text-gray-700 mb-2">Discount Ranges</h4>
                    {item.discountStructure && item.discountStructure.length > 0 ? (
                      <div className="space-y-1.5">
                        {item.discountStructure.map((range, idx) => (
                          <div
                            key={idx}
                            className="flex items-center gap-2 p-2 bg-gray-50 rounded-md"
                          >
                            <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                            <div className="flex-1">
                              <span className="text-xs text-gray-700">
                                {symbol}{range.min_order_value.toLocaleString()} to{" "}
                                {range.max_order_value == null
                                  ? "∞"
                                  : `${symbol}${range.max_order_value.toLocaleString()}`}
                              </span>
                            </div>
                            <span className="text-xs font-semibold text-brand">
                              {range.discount}% discount
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-xs text-gray-500 italic">No discount ranges available</div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </CustomModal>
  );
};

export default PreviousFySummaryModal;
